import styled from "styled-components";
import { breakpoints, colors } from "definitions";
import { addOpacityToColor } from "helpers";
import { HeaderSectionContainer } from "App/styles";

// Un solo azul, como el resto de las pantallas del jurado: el del footer.
const blue = colors.background.gray;

// El pendiente es lo único que pide atención: va en ámbar, que se distingue del
// azul sin leerse como un error.
const amber = "#B45F06";

const muted = "#7A8494";

const width = `
  width: 95%;
  max-width: 124.5rem;

  ${breakpoints.tablet} {
    width: 80%;
  }

  ${breakpoints.mobile} {
    width: 95%;
  }
`;

export const PageDescription = styled.p`
  ${width};
  margin: 3rem 0 0 0;
  font-family: Lato;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.fonts.darkGray};
`;

// Edición y buscador lado a lado, "Limpiar" al final.
export const Filter = styled(HeaderSectionContainer)`
  display: grid;
  grid-template-columns: minmax(0, 14rem) minmax(0, 1fr) auto;
  column-gap: 2rem;
  row-gap: 1.5rem;
  align-items: end;
  height: auto;
  margin: 2.4rem 0 0 0;

  button {
    margin: 0;
  }

  ${breakpoints.laptop} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Summary = styled.div`
  ${width};
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2.4rem;
`;

export const SummaryItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  padding: 1rem 1.6rem;
  border-radius: 8px;
  background: ${({ $tone }) =>
    $tone === "pending"
      ? addOpacityToColor(amber, 0.1)
      : addOpacityToColor(blue, 0.08)};
  color: ${({ $tone }) => ($tone === "pending" ? amber : colors.fonts.darkGray)};
  font-size: 1.4rem;

  strong {
    font-size: 2rem;
    color: ${({ $tone }) => ($tone === "pending" ? amber : blue)};
  }
`;

export const List = styled.div`
  ${width};
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding-top: 2rem;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 28rem) minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 2.4rem;
  row-gap: 1.2rem;
  padding: 1.8rem 2rem 1.8rem 2.4rem;
  border-radius: 6px;
  background: ${colors.background.white};
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.15);
  /* La franja de la izquierda dice de un vistazo si le falta algo. */
  border-left: 6px solid
    ${({ $pending }) => ($pending ? amber : addOpacityToColor(blue, 0.6))};

  ${breakpoints.laptop} {
    grid-template-columns: minmax(0, 1fr) auto;

    > :nth-child(2) {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }

  /* En el celular todo en una columna: jurado, premios y abajo el avance con
     el botón, repartidos a lo ancho. */
  ${breakpoints.phablet} {
    grid-template-columns: minmax(0, 1fr);
    padding: 1.6rem;

    > :nth-child(2) {
      grid-row: auto;
    }

    > :nth-child(3) {
      justify-content: space-between;
    }
  }
`;

export const Judge = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

export const JudgeName = styled.span`
  font-weight: bold;
  font-size: 1.7rem;
  color: #333333;
`;

export const JudgeEmail = styled.span`
  font-size: 1.3rem;
  color: #666666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Reminder = styled.span`
  font-size: 1.2rem;
  color: ${muted};
`;

export const Awards = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const chipTone = {
  voted: `
    background: ${addOpacityToColor(blue, 0.1)};
    color: ${blue};
    border-color: transparent;
  `,
  pending: `
    background: ${colors.background.white};
    color: ${amber};
    border-color: ${amber};
  `,
  idle: `
    background: #F2F4F7;
    color: ${muted};
    border-color: transparent;
  `,
};

export const AwardChip = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 1rem;
  border: 1px solid;
  border-radius: 999px;
  font-size: 1.3rem;
  line-height: 1.3;
  ${({ $tone }) => chipTone[$tone]};

  small {
    font-size: 1.1rem;
    opacity: 0.85;
  }

  svg {
    flex: 0 0 auto;
    width: 1.4rem;
    height: 1.4rem;
  }
`;

export const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

export const Progress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 9rem;
  font-size: 1.2rem;
  color: ${muted};
  text-align: right;

  ${breakpoints.phablet} {
    align-items: flex-start;
    text-align: left;
  }

  strong {
    font-size: 1.8rem;
    color: ${({ $pending }) => ($pending ? amber : blue)};
  }
`;

export const RemindButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  height: 3.8rem;
  padding: 0 1.4rem;
  border: 1px solid ${amber};
  border-radius: 8px;
  background: transparent;
  color: ${amber};
  font-family: inherit;
  font-size: 1.3rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease;

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }

  :hover:enabled {
    background: ${addOpacityToColor(amber, 0.1)};
  }

  :focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${addOpacityToColor(amber, 0.35)};
  }
`;

export const Done = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  height: 3.8rem;
  padding: 0 1.4rem;
  color: ${blue};
  font-size: 1.3rem;
  font-weight: 700;

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }
`;

export const ConfirmBox = styled.div`
  grid-column: 1 / -1;
`;

export const Empty = styled.div`
  ${width};
  margin-top: 2rem;
  padding: 3rem 2rem;
  border-radius: 6px;
  background: ${colors.background.white};
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.15);
  text-align: center;
  font-weight: bold;
  font-size: 1.6rem;
  color: #666666;
`;

export const Notice = styled.p`
  ${width};
  margin: 2rem 0 0 0;
  padding: 1.2rem 1.6rem;
  border-radius: 8px;
  background: ${addOpacityToColor(blue, 0.08)};
  color: ${colors.fonts.darkGray};
  font-size: 1.4rem;
`;
